/**
 * Combined connection-state for the header indicator (#1496).
 *
 * Composes:
 *   - navigator.onLine                         → offline
 *   - 'rdm:maintenance' event (from api.ts)    → maintenance
 *   - useSyncEngine().pendingCount/isSyncing   → syncing
 *   - default                                  → online (Live)
 *
 * The header chip (ApiStatusIndicator) reads this to render one of four
 * states. The popup uses pendingCount/lastSyncAt/syncNow for the
 * "X items waiting to sync" / "Sync Now" surface area.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSyncEngine } from './useSyncEngine';

export type ConnectionState = 'online' | 'syncing' | 'offline' | 'maintenance';

export interface UseConnectionStatusResult {
  state: ConnectionState;
  isOnline: boolean;
  isMaintenance: boolean;
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
  lastSyncAt: string | null;
  syncNow: () => Promise<void>;
}

// Treat a maintenance event as active for this long after the last fire.
// api.ts fires the event on each 503 / CORS-network failure; if we go this
// long without one we assume the backend recovered.
const MAINTENANCE_GRACE_MS = 60_000;

export function useConnectionStatus(): UseConnectionStatusResult {
  const sync = useSyncEngine();
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [maintenanceLastSeenAt, setMaintenanceLastSeenAt] = useState<number | null>(null);

  // navigator.onLine listeners
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // rdm:maintenance event listener — refreshed each time the API hits 503/CORS-down
  useEffect(() => {
    const onMaintenance = () => setMaintenanceLastSeenAt(Date.now());
    window.addEventListener('rdm:maintenance', onMaintenance);
    return () => window.removeEventListener('rdm:maintenance', onMaintenance);
  }, []);

  // Tick to expire maintenance state after the grace window
  useEffect(() => {
    if (maintenanceLastSeenAt === null) return;
    const expireAt = maintenanceLastSeenAt + MAINTENANCE_GRACE_MS;
    const remaining = expireAt - Date.now();
    if (remaining <= 0) {
      setMaintenanceLastSeenAt(null);
      return;
    }
    const timer = setTimeout(() => setMaintenanceLastSeenAt(null), remaining);
    return () => clearTimeout(timer);
  }, [maintenanceLastSeenAt]);

  const isMaintenance =
    maintenanceLastSeenAt !== null &&
    Date.now() - maintenanceLastSeenAt < MAINTENANCE_GRACE_MS;

  let state: ConnectionState;
  if (isMaintenance) state = 'maintenance';
  else if (!isOnline) state = 'offline';
  else if (sync.isSyncing || sync.pendingCount > 0) state = 'syncing';
  else state = 'online';

  const syncNow = useCallback(async () => {
    await sync.syncNow();
  }, [sync]);

  return {
    state,
    isOnline,
    isMaintenance,
    pendingCount: sync.pendingCount,
    failedCount: sync.failedCount,
    isSyncing: sync.isSyncing,
    lastSyncAt: sync.lastSyncAt,
    syncNow,
  };
}
